import { Component, OnInit } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { AngularMaterialModule } from 'src/app/material.module';
import { BaseComponent } from '@lib/components';
import { IAccount, ICustomer } from '@lib/interfaces';
import { ISenderInfo } from '@lib/interfaces/super-agent-transfer.interface';
import { SidePanelService } from '@lib/services';
import { currencyDecimals, formatPhoneNumber, maskFullName } from '@lib/utils';
import { ConfirmTransferComponent } from '../confirm-transfer/confirm-transfer.component';

// Reported minimum for the "Top-up for Agent" flow — currently only specified for IQD; other
// currencies keep the pre-existing 0.01 sanity-check minimum until a value is confirmed for them.
const MIN_TOPUP_AMOUNT_IQD = 2000;

@Component({
    selector: 'app-enter-amount',
    standalone: true,
    templateUrl: './enter-amount.component.html',
    styleUrls: ['./enter-amount.component.scss'],
    imports: [AngularMaterialModule, ReactiveFormsModule],
})
export class EnterAmountComponent extends BaseComponent implements OnInit {
    recipientInfo: ICustomer;
    senderAccounts: IAccount[];
    senderInfo: ISenderInfo;

    protected currencies: string[] = [];
    protected selectedCurrency = '';
    protected amountControl = new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]);
    protected noteControl = new FormControl('');

    constructor(private readonly dialogRef: MatDialogRef<EnterAmountComponent>, private readonly sidePanel: SidePanelService) {
        super();
    }

    ngOnInit(): void {
        this.currencies = Object.keys(this.senderAccounts?.[0]?.balances ?? {});
        this.selectedCurrency = this.currencies[0] ?? '';
        this.updateAmountValidators();
    }

    protected get availableBalance(): number {
        return this.senderAccounts?.[0]?.balances?.[this.selectedCurrency] ?? 0;
    }

    protected get isInsufficientBalance(): boolean {
        const amount = this.amountControl.value;
        return !!amount && amount > this.availableBalance;
    }

    protected get minimumAmount(): number {
        return this.selectedCurrency === 'IQD' ? MIN_TOPUP_AMOUNT_IQD : 0.01;
    }

    protected selectCurrency(currency: string): void {
        this.selectedCurrency = currency;
        this.updateAmountValidators();
    }

    // Re-applied whenever the currency changes since the minimum (IQD vs everything else) depends
    // on it — a validator set once in the field initializer wouldn't pick up later currency switches.
    private updateAmountValidators(): void {
        this.amountControl.setValidators([Validators.required, Validators.min(this.minimumAmount)]);
        this.amountControl.updateValueAndValidity();
    }

    // Native <select> instead of mat-select — matches the plain-HTML form controls the real
    // Peer to Peer flow uses (peer-to-peer.component.html), not Angular Material form fields.
    protected onCurrencyChange(event: Event): void {
        this.selectCurrency((event.target as HTMLSelectElement).value);
    }

    protected get maskedRecipientName(): string {
        return maskFullName(this.recipientInfo.first_name, this.recipientInfo.last_name);
    }

    protected get formattedRecipientMobile(): string {
        return formatPhoneNumber(this.recipientInfo.mobile);
    }

    protected get balanceDecimals(): string {
        return currencyDecimals(this.selectedCurrency);
    }

    protected confirm(): void {
        this.amountControl.markAsTouched();
        if (this.amountControl.invalid || this.isInsufficientBalance) return;

        this.dialogRef.close();
        const dialogRef = this.sidePanel.open(ConfirmTransferComponent);
        dialogRef.componentInstance.recipientInfo = this.recipientInfo;
        dialogRef.componentInstance.senderInfo = this.senderInfo;
        dialogRef.componentInstance.amount = this.amountControl.value as number;
        dialogRef.componentInstance.currency = this.selectedCurrency;
        dialogRef.componentInstance.note = this.noteControl.value || undefined;
    }

    protected cancel(): void {
        this.dialogRef.close();
    }
}
